import type { ReactNode } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Box, Stack, Typography } from '@mui/material';
import { ThemeToggle } from '../ui/ThemeToggle';
import { LanguageSwitcher } from '../ui/LanguageSwitcher';
import { useTranslation } from 'react-i18next';

export type AuthVariant = 'customer' | 'pro';

type Props = {
  /** Left side — the form, headlines, helper links. */
  children: ReactNode;
  /** Right side — marketing copy, benefits, illustration. */
  marketing: ReactNode;
  variant: AuthVariant;
};

/**
 * Split-screen auth chrome: brand bar on top, form on the left, marketing
 * panel on the right (desktop only). The right panel is hidden on mobile so
 * the form gets the full width.
 */
export function SplitAuthLayout({ children, marketing, variant }: Props) {
  const { t } = useTranslation();
  const isPro = variant === 'pro';

  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      {/* TOP BAR */}
      <Box
        sx={{
          py: 1.5,
          px: { xs: 2, md: 4 },
          borderBottom: '1px solid',
          borderColor: 'divider',
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          bgcolor: 'background.paper',
        }}
      >
        <Box
          component={RouterLink}
          to={isPro ? '/pro' : '/'}
          sx={{
            textDecoration: 'none',
            display: 'inline-flex',
            alignItems: 'center',
            gap: 0.75,
            color: 'text.primary',
          }}
          aria-label={isPro ? `${t('app.name')} PRO` : t('app.name')}
        >
          <Box
            component="img"
            src="/logo-noir.png"
            alt={t('app.name')}
            loading="eager"
            sx={{
              display: 'block',
              height: 44,
              width: 'auto',
              objectFit: 'contain',
            }}
          />
          {isPro && (
            <Typography
              component="span"
              sx={{
                fontWeight: 700,
                fontSize: '0.7rem',
                letterSpacing: '0.18em',
                color: 'primary.main',
              }}
            >
              PRO
            </Typography>
          )}
        </Box>
        <Box sx={{ flex: 1 }} />
        <LanguageSwitcher />
        <ThemeToggle size="small" />
      </Box>

      {/* BODY — split */}
      <Box
        sx={{
          flex: 1,
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
        }}
      >
        {/* LEFT — form */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'center',
            p: { xs: 3, md: 6 },
            bgcolor: 'background.default',
          }}
        >
          <Box sx={{ width: '100%', maxWidth: 440 }}>{children}</Box>
        </Box>

        {/* RIGHT — marketing (desktop only).
            • customer variant:
                light → saturated copper background (white text)
                dark  → deep warm near-black with a copper radial accent
                        (matches the rest of the dark UI; the bright copper
                        of light mode was too jarring against a dark form)
            • pro variant:
                light → soft tinted paper
                dark  → near-black warm */}
        <Box
          sx={{
            display: { xs: 'none', md: 'flex' },
            alignItems: 'center',
            justifyContent: 'center',
            p: { md: 6, lg: 8 },
            bgcolor: (theme) => {
              if (isPro) {
                return theme.palette.mode === 'dark'
                  ? '#0E0B08'
                  : theme.palette.background.paper;
              }
              // customer variant
              return theme.palette.mode === 'dark'
                ? '#1A1108'
                : theme.palette.primary.main;
            },
            color: (theme) => {
              if (isPro) {
                return theme.palette.mode === 'dark'
                  ? theme.palette.common.white
                  : theme.palette.text.primary;
              }
              // customer variant — white on copper OR white on dark warm.
              return theme.palette.common.white;
            },
            position: 'relative',
            overflow: 'hidden',
            borderLeft: (theme) =>
              theme.palette.mode === 'dark'
                ? 'none'
                : isPro
                ? `1px solid ${theme.palette.divider}`
                : 'none',
          }}
        >
          <Box sx={{ width: '100%', maxWidth: 520, position: 'relative', zIndex: 1 }}>
            {marketing}
          </Box>
          {/* Soft accent gradient — keeps the brand copper visible in dark
              mode and adds a subtle highlight on the bright copper of light
              mode. Choice of overlay:
                customer + light → white highlight on copper
                customer + dark  → copper highlight on near-black
                pro              → copper highlight (always) */}
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              opacity: (theme) =>
                isPro && theme.palette.mode === 'light' ? 0.15 : 0.4,
              backgroundImage: (theme) => {
                if (isPro) {
                  return 'radial-gradient(60% 60% at 80% 20%, rgba(232,155,90,0.25) 0%, transparent 65%)';
                }
                return theme.palette.mode === 'dark'
                  ? 'radial-gradient(60% 60% at 80% 20%, rgba(232,155,90,0.32) 0%, transparent 65%)'
                  : 'radial-gradient(60% 60% at 80% 20%, rgba(255,255,255,0.18) 0%, transparent 65%)';
              },
            }}
          />
        </Box>
      </Box>
    </Box>
  );
}

/**
 * Reusable benefit list with check icons — used inside marketing panels.
 *
 * Inherits the parent panel's text colour via `color: 'inherit'`, so the
 * same component reads correctly whether it sits on the dark pro panel,
 * the copper customer panel, or the light-mode pro panel.  The check
 * circle uses `currentColor` with low alpha so it tints itself based on
 * the surrounding text colour without needing a theme branch.
 */
export function MarketingBenefits({ items }: { items: string[] }) {
  return (
    <Stack spacing={1.75} sx={{ mt: 4, color: 'inherit' }}>
      {items.map((b) => (
        <Stack key={b} direction="row" spacing={1.5} alignItems="flex-start">
          <Box
            sx={{
              width: 24,
              height: 24,
              borderRadius: '50%',
              // Mix-blend trick: 18% alpha of the current text colour.
              // Falls back gracefully when color-mix isn't supported.
              bgcolor: 'color-mix(in srgb, currentColor 18%, transparent)',
              color: 'inherit',
              flexShrink: 0,
              mt: 0.25,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '0.95rem',
            }}
          >
            ✓
          </Box>
          <Typography
            variant="body1"
            sx={{ color: 'inherit', opacity: 0.92, lineHeight: 1.5 }}
          >
            {b}
          </Typography>
        </Stack>
      ))}
    </Stack>
  );
}
